import { useState, useEffect } from 'react';
import FormType from './FormType';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { ExclamationTriangleIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import useAnalytics from '../hooks/useAnalytics';

const PICTO_FORMS = {
  '16': '📦',
  '15': '🧰',
  '233': '🧰',
}

function Form({
  userAccountId,
  userName,
  userSectors,
  userRole,
  userMode,
  userServiceDeskName,
  deviceIsInWebView = false,
}) {
  const isTechnician = userRole === 'technician';
  const [serviceDesks, setServiceDesks] = useState([]);
  const [selectedServiceDeskId, setSelectedServiceDeskId] = useState(
    isTechnician ? localStorage.getItem('selected-service-desk') : null
  );
  const [formTypes, setFormTypes] = useState([]);
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [isLoading, setIsLoading] = useState(!isTechnician || selectedServiceDeskId);
  const [isFormTypeLoading, setIsFormTypeLoading] = useState(false);

  const [formTypeFields, setFormTypeFields] = useState(null);

  useAnalytics({
    currentUser: userName,
    project: userServiceDeskName,
    role: userRole,
    module: 'create-request',
  });

  const handleServiceDeskChange = (serviceDeskId) => {
    setSelectedServiceDeskId(serviceDeskId);
    localStorage.setItem('selected-service-desk', serviceDeskId);
    setFormTypes([]);
    setSelectedFormType(null);
  };

  useEffect(() => {
    if (!isTechnician) return;

    const fetchServiceDesks = async () => {
      try {
        const response = await fetchWithAuth('service-desks');
        const result = await response.json();
        const desks = result.data || [];
        setServiceDesks(desks);

        // Auto-select if only one service desk available and nothing in localStorage
        if (desks.length === 1 && !selectedServiceDeskId) {
          const firstDeskId = String(desks[0].id);
          setSelectedServiceDeskId(firstDeskId);
          localStorage.setItem('selected-service-desk', firstDeskId);
        }
      } catch (error) {
        console.error('Error fetching service desks:', error);
      }
    };

    fetchServiceDesks();
  }, [isTechnician]);

  useEffect(() => {
    if (isTechnician && !selectedServiceDeskId) {
      setIsLoading(false);
      return;
    }

    const fetchFormTypes = async () => {
      try {
        const url = isTechnician && selectedServiceDeskId
          ? `form-types?serviceDeskId=${selectedServiceDeskId}`
          : 'form-types';

        const response = await fetchWithAuth(url, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Account-Id': userAccountId,
          },
        });
        const data = await response.json();

        // sort data.values to ensure that name containing "Intervention" is first
        data.values.sort((a, b) => a.name.includes('Intervention') ? -1 : 1);

        setFormTypes(data.values);
      } catch (error) {
        console.error('Error fetching form types:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormTypes();
  }, [selectedServiceDeskId]);

  useEffect(() => {
    if (!selectedFormType) return;

    setIsFormTypeLoading(true);
    const fetchFormTypeFields = async () => {
      const url = isTechnician && selectedServiceDeskId
        ? `form-types/${selectedFormType}?serviceDeskId=${selectedServiceDeskId}`
        : `form-types/${selectedFormType}`;

      const response = await fetchWithAuth(url);
      const data = await response.json();

      if (data && Array.isArray(data.requestTypeFields)) {
        setFormTypeFields(data);
      } else {
        console.error('[Form] Invalid formTypeFields data structure:', data);
        setFormTypeFields({ requestTypeFields: [] });
      }

      setIsFormTypeLoading(false);
    };

    fetchFormTypeFields();
  }, [selectedFormType, isTechnician, selectedServiceDeskId]);

  if (userMode === 'r') {
    return (
      <div className="h-full flex flex-col items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-6">
            <ExclamationTriangleIcon className="w-8 h-8 text-gray-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Accès restreint</h3>
          <p className="text-gray-600 leading-relaxed">
            Vous n'avez pas la permission de créer des demandes. Contactez votre administrateur pour plus d'informations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="w-full max-w-7xl mx-auto p-4">
          {/* Service Desk Selector for Technicians */}
          {isTechnician && !selectedFormType && (
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Nouvelle demande</h1>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <label htmlFor="service-desk-select" className="block text-sm font-medium text-gray-900 mb-2">
                  Sélectionnez un projet
                </label>
                <select
                  id="service-desk-select"
                  value={selectedServiceDeskId || ''}
                  onChange={(e) => handleServiceDeskChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                >
                  <option value="" disabled>
                    Sélectionnez un projet
                  </option>
                  {serviceDesks.map((sd) => (
                    <option key={sd.id} value={sd.id}>
                      {sd.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Header */}
          {!isTechnician && !selectedFormType && !isLoading && (
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900">Nouvelle demande</h1>
              <p className="text-gray-600">Sélectionnez le type de demande que vous souhaitez créer</p>
            </div>
          )}

          {/* Loading state */}
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="animate-pulse bg-white rounded-2xl border border-gray-200 h-64"
                />
              ))}
            </div>
          )}

          {/* Empty state for technicians without service desk */}
          {isTechnician && !selectedServiceDeskId && !isLoading && (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500 text-center">
                Veuillez sélectionner un projet pour afficher les types de demande disponibles.
              </p>
            </div>
          )}

          {/* Form type selection */}
          {!selectedFormType && !isLoading && formTypes.length > 0 && (
            <div className={`grid grid-cols-1 w-full ${formTypes.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-6`}>
              {formTypes.map((formType) => (
                <button
                  key={formType.id}
                  onClick={() => setSelectedFormType(formType.id)}
                  className="group relative bg-white rounded-2xl border-2 border-gray-200 p-8 hover:border-black hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                >
                  <div className="flex flex-col items-center text-center">
                    {/* Icon/Emoji */}
                    {/* {PICTO_FORMS[formType.id] && (
                      <div className="text-7xl mb-6 transform group-hover:scale-110 transition-transform duration-300">
                        {PICTO_FORMS[formType.id]}
                      </div>
                    )} */}

                    {/* Title */}
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-black transition-colors">
                      {formType.name}
                    </h3>

                    {/* Description */}
                    {formType.description && (
                      <p className="text-sm text-gray-600 leading-relaxed mb-4">
                        {formType.description}
                      </p>
                    )}

                    {/* Arrow indicator */}
                    <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-gray-900 group-hover:text-black">
                      <span>Continuer</span>
                      <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Selected form type */}
          {selectedFormType && (
            <div className="flex flex-col items-center mb-8">
              <div className="w-full max-w-4xl">
                <h2 className="text-2xl font-bold mb-8 text-gray-900 text-center">
                  {formTypes.find(formType => formType.id === selectedFormType)?.name}
                </h2>

                {/* Loading state for form fields */}
                {isFormTypeLoading && (
                  <div className="space-y-6">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="animate-pulse bg-white rounded-xl border border-gray-200 h-20"
                      />
                    ))}
                  </div>
                )}

                {/* Form fields */}
                {formTypeFields && (
                  <FormType
                    formTypeFields={formTypeFields.requestTypeFields}
                    selectedFormType={selectedFormType}
                    onCancel={() => setSelectedFormType(null)}
                    userAccountId={userAccountId}
                    userSectors={userSectors}
                    userRole={userRole}
                    deviceIsInWebView={deviceIsInWebView}
                    serviceDeskId={isTechnician ? selectedServiceDeskId : null}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Form;